import { useEffect, useState } from "react";
import type { ApprovalItem } from "./fixtures-deep";
import { StatusDot } from "./badges";
import {
  useApprovals,
  useDecideApproval,
  useActivity,
  useAgentsList,
} from "./useAgentsUi";

const UNDO_SECS = 6;

// ── Approvals (cross-agent sign-off queue) ────────────────────────────────────
export function ApprovalsView() {
  const { data: items = [], isLoading } = useApprovals();
  const decide = useDecideApproval();

  if (isLoading) return <div className="agui-stub">Loading sign-offs…</div>;
  if (items.length === 0) {
    return (
      <div className="agui-stub agui-stub--view">
        Nothing waiting on you. When an agent prepares a consequential action,
        it queues here for sign-off.
      </div>
    );
  }

  const clear = items.filter((i) => i.provenanceFlags.length === 0);
  const flaggedCount = items.length - clear.length;

  return (
    <div className="agui-approvals">
      <div className="agui-approvals__h">
        <span className="agui-block__label">
          {items.length} ready — review each
        </span>
        {clear.length > 1 ? (
          <button
            type="button"
            className="agui-btn"
            onClick={() =>
              clear.forEach((i) =>
                decide.mutate({ id: i.id, decision: "approved" }),
              )
            }
          >
            Approve all clear ({clear.length})
          </button>
        ) : null}
      </div>
      {flaggedCount > 0 ? (
        <div className="agui-approvals__note">
          ⚠ {flaggedCount} flagged item{flaggedCount > 1 ? "s" : ""} need
          individual review — not included in “approve all”.
        </div>
      ) : null}
      <div className="agui-approvals__list">
        {items.map((a) => (
          <ApprovalCard key={a.id} approval={a} />
        ))}
      </div>
    </div>
  );
}

function ApprovalCard({ approval }: { approval: ApprovalItem }) {
  const decide = useDecideApproval();
  const email = approval.email;
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(email?.body ?? "");
  const [grant, setGrant] = useState(false);
  const [pending, setPending] = useState<{
    decision: "approved" | "edited";
    secs: number;
  } | null>(null);

  // Undo window: hold the decision locally and only commit after the countdown.
  useEffect(() => {
    if (!pending) return;
    if (pending.secs <= 0) {
      // On "edited", carry the user's edits so they persist server-side (not just the status).
      decide.mutate({
        id: approval.id,
        decision: pending.decision,
        preparedPayload:
          pending.decision === "edited" && email
            ? { to: email.to, subject: email.subject, body }
            : undefined,
      });
      return;
    }
    const t = setTimeout(
      () => setPending((p) => (p ? { ...p, secs: p.secs - 1 } : p)),
      1000,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const recipientFlag = approval.provenanceFlags.find(
    (f) => f.param === "recipient",
  );

  if (pending) {
    return (
      <div className="agui-appr agui-appr--done">
        <span className="agui-appr__check">✓</span>
        <span>
          {pending.decision === "edited"
            ? "Approved with your edits"
            : "Approved"}{" "}
          — sending in {pending.secs}s
        </span>
        <button
          type="button"
          className="agui-link"
          onClick={() => setPending(null)}
        >
          Undo
        </button>
      </div>
    );
  }

  return (
    <div className="agui-appr">
      <div className="agui-appr__head">
        <StatusDot color={approval.agentColor} />
        <span className="agui-appr__agent">{approval.agentName}</span>
        <span className="agui-chip agui-tag agui-tag--signoff">
          {approval.actionType}
        </span>
      </div>

      <div className="agui-appr__why">
        <div className="agui-detline">
          <span className="agui-detline__k">Why</span>
          <span>{approval.why.trigger}</span>
        </div>
        <div className="agui-detline">
          <span className="agui-detline__k">It read</span>
          <span>{approval.why.read.join(" · ")}</span>
        </div>
      </div>

      {email ? (
        <div className="agui-appr__email">
          <div className="agui-appr__row">
            <span className="agui-appr__k">To</span>
            <span>
              {email.to}
              {recipientFlag ? (
                <span className="agui-prov">⚠ {recipientFlag.note}</span>
              ) : null}
            </span>
          </div>
          <div className="agui-appr__row">
            <span className="agui-appr__k">Subject</span>
            <span>{email.subject}</span>
          </div>
          {editing ? (
            <textarea
              className="agui-appr__bodyedit"
              rows={7}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              aria-label="Email body"
            />
          ) : (
            <div className="agui-appr__body">{body}</div>
          )}
          {email.sendTime ? (
            <div className="agui-appr__when">Send time: {email.sendTime}</div>
          ) : null}
        </div>
      ) : null}

      {approval.grantOffer ? (
        <label className="agui-appr__grant">
          <input
            type="checkbox"
            checked={grant}
            onChange={() => setGrant((g) => !g)}
          />
          <span>{approval.grantOffer}</span>
        </label>
      ) : null}

      <div className="agui-appr__actions">
        {editing ? (
          <>
            <button
              type="button"
              className="agui-btn agui-btn--primary"
              onClick={() => {
                setEditing(false);
                setPending({ decision: "edited", secs: UNDO_SECS });
              }}
            >
              Save &amp; approve
            </button>
            <button
              type="button"
              className="agui-btn"
              onClick={() => {
                setEditing(false);
                setBody(email?.body ?? "");
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="agui-btn agui-btn--primary"
              onClick={() =>
                setPending({ decision: "approved", secs: UNDO_SECS })
              }
            >
              Approve
            </button>
            {email ? (
              <button
                type="button"
                className="agui-btn"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            ) : null}
            <button
              type="button"
              className="agui-btn agui-btn--danger"
              onClick={() =>
                decide.mutate({ id: approval.id, decision: "rejected" })
              }
            >
              Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Activity (cross-agent history — Gmail-style, filterable) ──────────────────
type TimeWin = "anytime" | "today" | "week" | "month";
const TIME_LABEL: Record<TimeWin, string> = {
  anytime: "Anytime",
  today: "Today",
  week: "This week",
  month: "This month",
};

/** Time-window predicate over an item's (synthetic, in fixtures) timestamp. */
function inWindow(ts: string | undefined, win: TimeWin): boolean {
  if (win === "anytime" || !ts) return true;
  const age = Date.now() - new Date(ts).getTime();
  const day = 86_400_000;
  if (win === "today") return age < day;
  if (win === "week") return age < 7 * day;
  return age < 30 * day; // month
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ActivityView({
  initialAgentId,
}: {
  /** Pre-filter to one agent (set by a routine's "Open in Activity →"). */
  initialAgentId?: string;
} = {}) {
  const { data: items = [], isLoading } = useActivity();
  const { data: agents = [] } = useAgentsList();
  const [open, setOpen] = useState(false);
  const [agentFilter, setAgentFilter] = useState<string>(
    initialAgentId ?? "all",
  );
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [timeFilter, setTimeFilter] = useState<TimeWin>("anytime");

  if (isLoading) return <div className="agui-stub">Loading activity…</div>;

  // Task "types" available to filter on = the distinct run statuses in the feed.
  const types = Array.from(new Set(items.map((i) => i.status)));

  const shown = items.filter(
    (i) =>
      (agentFilter === "all" || i.agentId === agentFilter) &&
      (typeFilter === "all" || i.status === typeFilter) &&
      inWindow(i.ts, timeFilter),
  );
  const activeCount =
    (agentFilter !== "all" ? 1 : 0) +
    (typeFilter !== "all" ? 1 : 0) +
    (timeFilter !== "anytime" ? 1 : 0);

  return (
    <div className="agui-activity">
      <div className="agui-activity__bar">
        <span className="agui-activity__count">
          {shown.length} event{shown.length === 1 ? "" : "s"}
        </span>
        <div className="agui-filterwrap">
          <button
            type="button"
            className={`agui-filterbtn${activeCount ? " is-on" : ""}`}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            Filter
            {activeCount ? (
              <span className="agui-filterbtn__badge">{activeCount}</span>
            ) : null}
            <span aria-hidden="true"> ▾</span>
          </button>
          {open ? (
            <>
              <button
                type="button"
                className="agui-filter__backdrop"
                aria-label="Close filters"
                onClick={() => setOpen(false)}
              />
              <div className="agui-filterpanel" role="menu">
                <FilterGroup
                  label="Agent"
                  value={agentFilter}
                  onChange={setAgentFilter}
                  options={[
                    { value: "all", label: "All agents" },
                    ...agents.map((a) => ({ value: a.id, label: a.name })),
                  ]}
                />
                <FilterGroup
                  label="Time"
                  value={timeFilter}
                  onChange={(v) => setTimeFilter(v as TimeWin)}
                  options={(Object.keys(TIME_LABEL) as TimeWin[]).map((w) => ({
                    value: w,
                    label: TIME_LABEL[w],
                  }))}
                />
                <FilterGroup
                  label="Type"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={[
                    { value: "all", label: "All types" },
                    ...types.map((t) => ({ value: t, label: cap(t) })),
                  ]}
                />
                {activeCount ? (
                  <button
                    type="button"
                    className="agui-link agui-filterpanel__clear"
                    onClick={() => {
                      setAgentFilter("all");
                      setTypeFilter("all");
                      setTimeFilter("anytime");
                    }}
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="agui-stub agui-stub--view">
          No activity matches these filters.
        </div>
      ) : (
        <ul className="agui-actfeed">
          {shown.map((i) => (
            <li key={i.id} className="agui-actrow">
              <StatusDot color={i.agentColor} />
              <span className="agui-actrow__when">{i.when}</span>
              <span className="agui-actrow__agent">{i.agentName}</span>
              <span className="agui-actrow__sum">{i.summary}</span>
              <span className={`agui-run__status agui-run__status--${i.status}`}>
                {i.status === "auto" && i.trusted ? "auto · trusted" : i.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterGroup({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="agui-filtergroup">
      <div className="agui-filtergroup__label">{label}</div>
      <div className="agui-filtergroup__opts">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`agui-filteropt${value === o.value ? " is-on" : ""}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
